import React from "react";
import { connect } from "react-redux";
import { Dropdown } from "react-bootstrap";
import { FiCheck, FiChevronLeft, FiChevronRight, FiMoreHorizontal, FiPlus } from "react-icons/fi";
import GroupSupervisorEdit from "./GroupSupervisorEditModal";
import { showModal, hideModal } from "actions/modalActions";
import Avatar from "components/Avatar";
import DataTable from "components/DataTable";
import * as groupsActions from "actions/groupsActions";
import { EmployeeLink } from "components/Employees/EmployeeEditDrawer";
import { groupsService } from "services";
import { errorFormating } from "utils/utils";
import { withTranslation } from "react-i18next";
import {
	getActiveSupervisorPermissionGroups,
	packSupervisorsByPermissionBudget,
} from "./supervisorPermissions";

function compareSupervisorNames(a, b) {
	const nameA = `${a.employee?.name || ""} ${a.employee?.surname || ""}`.trim();
	const nameB = `${b.employee?.name || ""} ${b.employee?.surname || ""}`.trim();
	return nameA.localeCompare(nameB, "es", {
		numeric: true,
		sensitivity: "base",
	});
}

class GroupSupervisors extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			page: 0,
			sort: null,
		};

		this.closeModal = this.closeModal.bind(this);
		this.openNewSupervisorModal = this.openNewSupervisorModal.bind(this);
		this.openAlertModal = this.openAlertModal.bind(this);
	}

	componentDidMount() {
		this.getSupervisors();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.group?.id !== this.props.group?.id) {
			this.setState({ page: 0, sort: null });
			return;
		}

		if (prevProps.group_supervisors !== this.props.group_supervisors) {
			const totalPages = this.getTotalPages();
			if (this.state.page > totalPages - 1) {
				this.setState({ page: Math.max(0, totalPages - 1) });
			}
		}
	}

	async getSupervisors() {
		const groupId = this.props.group.id;
		await this.props.getGroupSupervisors(groupId);
	}

	closeModal() {
		this.props.hideModal();
	}

	openNewSupervisorModal() {
		const { group } = this.props;

		this.props.showModal(
			{
				open: true,
				title: this.props.t("groups.supervisors.newSupervisor"),
				style: { width: "480px" },
				content: (
					<GroupSupervisorEdit handleClose={this.closeModal} group={group} />
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	}

	openEditSupervisorModal(group_supervisor) {
		const { group } = this.props;

		this.props.showModal(
			{
				open: true,
				title: this.props.t("groups.supervisors.editSupervisor"),
				style: { width: "480px" },
				content: (
					<GroupSupervisorEdit
						group_supervisor={group_supervisor}
						group={group}
						handleClose={this.closeModal}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	}

	openDeleteSupervisorModal(groupSupervisorId) {
		this.props.showModal(
			{
				open: true,
				style: { width: "350px" },
				title: this.props.t("groups.supervisors.deleteSupervisor"),
				message: this.props.t("groups.supervisors.deleteConfirmation"),
				deleteAction: () => this.confirmDelete(groupSupervisorId),
				closeModal: this.closeModal,
			},
			"delete"
		);
	}

	openAlertModal(message) {
		this.props.showModal(
			{
				open: true,
				title: this.props.t("groups.deleteError"),
				style: { width: "400px" },
				message: message,
				closeModal: this.closeModal,
			},
			"alert"
		);
	}

	confirmDelete = async (groupSupervisorId) => {
		const groupId = this.props.group.id;

		const response = await groupsService.removeSupervisor(
			groupId,
			groupSupervisorId
		);

		if (!response.ok) {
			let textError = errorFormating(response);
			this.openAlertModal(textError.id);
		} else {
			this.getSupervisors();
			this.closeModal();
		}
	};

	renderSupervisorCell = (group_supervisor) => {
		const employee = group_supervisor.employee || {};
		const fullName = `${employee.name || ""} ${employee.surname || ""}`.trim();

		return (
			<div className="group-supervisor-identity">
				<Avatar
					userId={employee.id}
					className="avatar-sm"
					alt={fullName}
				/>
				<div className="group-supervisor-identity__text">
					<EmployeeLink className="table-link" employee={employee}>
						{fullName}
					</EmployeeLink>
					{employee.email && (
						<span className="group-supervisor-identity__email">
							{employee.email}
						</span>
					)}
				</div>
			</div>
		);
	};

	renderPermissionChips = (group_supervisor) => {
		const { t, group } = this.props;
		const activeGroups = getActiveSupervisorPermissionGroups(
			group_supervisor,
			group.two_levels_workflow
		);

		if (activeGroups.length === 0) {
			return (
				<span className="group-supervisor-perm-empty">
					{t("groups.supervisors.noActivePermissions")}
				</span>
			);
		}

		return (
			<div className="group-supervisor-perm-groups">
				{activeGroups.map((permissionGroup, index) => {
					const GroupIcon = permissionGroup.icon;
					return (
						<React.Fragment key={permissionGroup.key}>
							{index > 0 && (
								<span
									className="group-supervisor-perm-divider"
									aria-hidden
								/>
							)}
							<div className="group-supervisor-perm-group">
								<div className="group-supervisor-perm-group__label">
									<GroupIcon />
									<span>{t(permissionGroup.labelKey)}</span>
								</div>
								<div className="group-supervisor-perm-group__chips">
									{permissionGroup.permissions.map((permission) => (
										<span
											key={permission.id}
											className="group-supervisor-perm-chip"
										>
											<span
												className="group-supervisor-perm-chip__check"
												aria-hidden
											>
												<FiCheck />
											</span>
											{t(permission.labelKey)}
										</span>
									))}
								</div>
							</div>
						</React.Fragment>
					);
				})}
			</div>
		);
	};

	getSortedSupervisors = () => {
		const rows = this.props.group_supervisors || [];
		const { sort } = this.state;
		if (!sort || sort.key !== "supervisor") {
			return rows;
		}
		const sorted = [...rows].sort(compareSupervisorNames);
		return sort.direction === "desc" ? sorted.reverse() : sorted;
	};

	getPackedPages = () => {
		return packSupervisorsByPermissionBudget(
			this.getSortedSupervisors(),
			this.props.group?.two_levels_workflow
		);
	};

	getTotalPages = () => {
		return Math.max(1, this.getPackedPages().length);
	};

	getPagedSupervisors = () => {
		const pages = this.getPackedPages();
		if (pages.length === 0) {
			return [];
		}
		return pages[Math.min(this.state.page, pages.length - 1)] || [];
	};

	handleSortChange = (next) => {
		this.setState({ sort: next, page: 0 });
	};

	goToPage = (page) => {
		const totalPages = this.getTotalPages();
		const nextPage = Math.min(Math.max(0, page), totalPages - 1);
		this.setState({ page: nextPage });
	};

	getColumns = () => [
		{
			key: "supervisor",
			header: this.props.t("groups.supervisors.columnSupervisor"),
			className: "td-name",
			sortAccessor: (row) =>
				`${row.employee?.name || ""} ${row.employee?.surname || ""}`.trim(),
			render: this.renderSupervisorCell,
		},
		{
			key: "permissions",
			header: this.props.t("groups.supervisors.columnActivePermissions"),
			sortable: false,
			render: this.renderPermissionChips,
		},
	];

	renderActions = (group_supervisor) => (
		<Dropdown>
			<Dropdown.Toggle variant="action">
				<FiMoreHorizontal />
			</Dropdown.Toggle>
			<Dropdown.Menu>
				<Dropdown.Item
					as="button"
					onClick={() => this.openEditSupervisorModal(group_supervisor)}
				>
					{this.props.t("common.edit")}
				</Dropdown.Item>
				<Dropdown.Item
					as="button"
					onClick={() => this.openDeleteSupervisorModal(group_supervisor.id)}
				>
					{this.props.t("common.delete")}
				</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown>
	);

	renderPager = (count) => {
		const { t } = this.props;
		const { page } = this.state;
		const totalPages = this.getTotalPages();
		const showPager = totalPages > 1;

		return (
			<div className="group-supervisors-footer">
				<span className="group-supervisors-footer__count">
					{t("groups.supervisors.count", { count })}
				</span>
				{showPager && (
					<div className="group-supervisors-pager">
						<button
							type="button"
							className="group-supervisors-pager__btn"
							onClick={() => this.goToPage(page - 1)}
							disabled={page === 0}
							aria-label={t("groups.supervisors.previousPage")}
						>
							<FiChevronLeft />
						</button>
						<span
							className="group-supervisors-pager__page"
							aria-label={t("groups.supervisors.page", {
								current: page + 1,
								total: totalPages,
							})}
						>
							{page + 1}
						</span>
						<button
							type="button"
							className="group-supervisors-pager__btn"
							onClick={() => this.goToPage(page + 1)}
							disabled={page >= totalPages - 1}
							aria-label={t("groups.supervisors.nextPage")}
						>
							<FiChevronRight />
						</button>
					</div>
				)}
			</div>
		);
	};

	render() {
		const { group_supervisors, t } = this.props;
		if (!group_supervisors) {
			return null;
		}

		const count = group_supervisors.length;
		const pagedSupervisors = this.getPagedSupervisors();

		return (
			<div className="group-supervisors">
				<div className="group-supervisors-header">
					<div className="group-supervisors-header__copy">
						<h3 className="group-supervisors-header__title">
							{t("groups.tabs.supervisors")}
						</h3>
						<p className="group-supervisors-header__description">
							{t("groups.supervisors.listDescription")}
						</p>
					</div>
					<button
						type="button"
						className="btn btn-primary group-supervisors-header__cta"
						onClick={this.openNewSupervisorModal}
					>
						<FiPlus />
						{t("groups.supervisors.newSupervisor")}
					</button>
				</div>

				<div className={`group-supervisors-table${count > 0 ? " group-supervisors-table--paged" : ""}`}>
					<DataTable
						tableClassName="table-groups table-group-supervisors table-hover"
						tableStyle={{ backgroundColor: "#ffffff" }}
						columns={this.getColumns()}
						rows={pagedSupervisors}
						rowKey={(row) => row.id}
						sortState={this.state.sort}
						onSortChange={this.handleSortChange}
						emptyMessage={t("groups.supervisors.noSupervisors")}
						actionsColumn={{
							render: this.renderActions,
							className: "td-actions",
						}}
					/>
					{count > 0 && this.renderPager(count)}
				</div>
			</div>
		);
	}
}

const mapStateToProps = (reducers) => {
	return {
		...reducers.groupsReducer,
		user: reducers.authReducer.user,
	};
};

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }));
	},
	getGroupSupervisors: (id) => dispatch(groupsActions.getGroupSupervisors(id)),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(withTranslation()(GroupSupervisors));
